/* My Bookshelf + Maven controls — `/bookstore/account`.
 *
 * Owned books grid, Weekly Maven status panel (with claim-this-week
 * picker when active), and quick links back to genres.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, BookOpen, Loader2, Sparkles, LogOut, ShoppingBag, Clock,
  CheckCircle2, BarChart3, Heart, X,
} from 'lucide-react';
import { useSeoMeta } from '@/lib/useSeoMeta';
import { useBookstoreAuth } from '@/lib/bookstoreAuthContext';
import { bookstoreApi, resolveCoverUrl } from '@/lib/bookstoreApi';
import { BOOKSTORE_READING_LANGUAGES } from '@/pages/bookstore/GenrePage';
import BookDetailModal from '@/components/bookstore/BookDetailModal';
import HeatPreferencePanel from '@/components/bookstore/HeatPreferencePanel';
// iter-romance25 — Reader warning manager surfaces localStorage
// preferences here so readers can curate them without visiting every
// shelf. Import the hook + a small manager component below.
import useReaderTagPreferences from '@/lib/useReaderTagPreferences';
import useHideMatureShelves from '@/lib/useHideMatureShelves';   // Feb-2026 · MA super-tile toggle
import { ShieldAlert } from 'lucide-react';
// iter-romance25 — CoachHint wraps the manager with a "what to do
// next" mentor prompt on hover.
import CoachHint from '@/components/coach/CoachHint';
// iter-romance25 — Fire the manager tour on first mount.
import { runTour } from '@/lib/tours';

function ShelfCard({ entry, onOpen }) {
  const b = entry.book;
  const front = resolveCoverUrl(b.cover_front_url);
  const [busy, setBusy] = React.useState(false);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await bookstoreApi.shelfReaderOpen(b.id);
      if (r?.ready && r.redirect_url) {
        toast.success(r.title ? `Opening "${r.title}"…` : 'Opening in your Reader…');
        window.location.href = r.redirect_url;
        return; // navigation in progress — leave spinner showing
      }
      // Companion not configured or push failed — fall back to detail modal
      onOpen(b);
    } catch {
      onOpen(b); // network error — graceful fallback
    } finally {
      setBusy(false);
    }
  };

  return (
    <button onClick={handleClick}
            disabled={busy}
            data-testid={`shelf-book-${b.id}`}
            className="group flex flex-col rounded-md border border-border bg-card p-2 text-left transition hover:border-primary/40 hover:shadow-panel-active disabled:opacity-70">
      <div className="relative aspect-[2/3] overflow-hidden rounded">
        {front
          ? <img src={front} alt={b.title} className="h-full w-full object-cover" />
          : <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/15 to-secondary text-primary"><BookOpen className="h-8 w-8" /></div>}
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <Loader2 className="h-5 w-5 animate-spin text-white" />
          </div>
        )}
        <div className="absolute right-1 top-1 rounded-sm bg-card/85 px-1 py-0.5 text-[9px] uppercase tracking-wider text-muted-foreground">
          {entry.acquired_via}
        </div>
      </div>
      <div className="mt-2 line-clamp-2 text-[12px] font-semibold tracking-tight">{b.title}</div>
      <div className="text-[10.5px] text-muted-foreground">{b.author}</div>
    </button>
  );
}

export default function BookstoreAccount() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, checking, logout, refresh } = useBookstoreAuth();
  const [shelf, setShelf] = useState(null);
  const [eligible, setEligible] = useState(null);
  const [open, setOpen] = useState(null);
  // iter-rh1 — brief "taking you to your Reader" transition while we
  // attempt the Companion Reader handoff after a purchase.
  const [handoff, setHandoff] = useState(null);

  useSeoMeta('/bookstore/account', {
    title: 'My bookshelf · Crown & Rose',
    description: 'View your bookshelf, claim your Weekly Maven pick, and manage your subscription.',
  });

  const reload = useCallback(async () => {
    try {
      const s = await bookstoreApi.bookshelf();
      setShelf(s.shelf || []);
    } catch (e) { console.debug('[bookshelf] not loaded:', e?.message); }
    if (user?.maven_status === 'active') {
      try {
        const el = await bookstoreApi.mavenEligible();
        setEligible(el);
      } catch (e) { console.debug('[maven-eligible] check failed:', e?.message); }
    } else {
      setEligible(null);
    }
  }, [user]);

  useEffect(() => {
    if (checking) return;
    if (!user) navigate('/bookstore/login?next=/bookstore/account', { replace: true });
    else reload();
  }, [user, checking, navigate, reload]);

  // Refresh after a successful purchase/maven return
  useEffect(() => {
    const sessionId = params.get('session_id');
    if (params.get('purchase') === 'success' || params.get('maven') === 'success') {
      // Stripe webhook updates may take a moment; refresh a couple of times.
      const t1 = setTimeout(() => { refresh(); reload(); }, 1500);
      const t2 = setTimeout(() => { refresh(); reload(); }, 4500);
      toast.success(params.get('maven') === 'success' ? 'Welcome, Weekly Maven!' : 'Purchase confirmed.');

      // iter-rh1 — Best-effort hand-off to the Companion Reader app so
      // the buyer lands straight on the book they just bought. Quietly
      // falls back to this page if that app isn't wired up yet.
      if (params.get('purchase') === 'success' && sessionId) {
        setHandoff({ redirecting: true, title: null });
        bookstoreApi.readerHandoff(sessionId)
          .then((r) => {
            if (r?.ready && r.redirect_url) {
              setHandoff({ redirecting: true, title: r.title || null });
              window.setTimeout(() => { window.location.href = r.redirect_url; }, 900);
            } else {
              setHandoff(null);
            }
          })
          .catch(() => setHandoff(null));
      }
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [params, refresh, reload]);

  if (checking || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (handoff?.redirecting) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 text-center px-6"
           data-testid="bs-reader-handoff-overlay">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <div className="text-[14px] font-semibold" data-testid="bs-reader-handoff-title">
          {handoff.title ? `Opening "${handoff.title}" in your Reader…` : 'Taking you to your Reader…'}
        </div>
        <p className="text-[12px] text-muted-foreground">One moment — you&rsquo;ll land right where you left off.</p>
      </div>
    );
  }

  const claim = async (book) => {
    try {
      await bookstoreApi.mavenClaim(book.id);
      toast.success(`Added "${book.title}" to your shelf.`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not claim that book.');
    }
  };

  const startMaven = async () => {
    try {
      const { url } = await bookstoreApi.subscribeMaven(window.location.origin, user.preferred_genre);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start Maven checkout.');
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground pb-[max(6rem,env(safe-area-inset-bottom))]" data-testid="bs-account-page">
      <header className="sticky top-0 z-40 border-b border-border/60 bg-card/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <Link to="/bookstore" className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3 w-3" /> Back to the Book Store
          </Link>
          <button onClick={async () => { await logout(); navigate('/bookstore'); }}
                  data-testid="bs-account-logout"
                  className="inline-flex items-center gap-1 rounded-sm px-2.5 py-1 text-[12px] text-muted-foreground hover:bg-secondary hover:text-foreground">
            <LogOut className="h-3 w-3" /> Sign out
          </button>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 py-10">
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Signed in as {user.email}</div>
        <h1 className="text-3xl font-semibold tracking-tight">Welcome, {user.name}.</h1>

        {/* Maven status */}
        <div className="mt-6 rounded-xl border border-border bg-card p-5" data-testid="bs-account-maven">
          {user.maven_status === 'active' ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-primary">
                    <Sparkles className="h-3 w-3" /> Weekly Maven · Active
                  </div>
                  <div className="mt-1 text-[15px] font-semibold tracking-tight">
                    {eligible?.can_claim_now
                      ? 'Your weekly pick is waiting.'
                      : `Next claim opens ${eligible?.reason?.replace('next_claim_in_', 'in ').replace('_days', ' days') || 'next week'}.`}
                  </div>
                </div>
                <div className="text-right text-[11px] text-muted-foreground">
                  Renews {user.maven_current_period_end ? new Date(user.maven_current_period_end).toLocaleDateString() : '—'}
                </div>
              </div>
              {eligible?.can_claim_now && eligible.eligible?.length > 0 && (
                <div className="mt-4">
                  <div className="mb-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Available this week</div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                    {eligible.eligible.slice(0, 12).map((b) => (
                      <button key={b.id} onClick={() => claim(b)}
                              data-testid={`bs-claim-${b.id}`}
                              className="group flex flex-col rounded border border-border bg-background p-1.5 text-left transition hover:border-primary/50">
                        <div className="relative aspect-[2/3] overflow-hidden rounded-sm">
                          {b.cover_front_url
                            ? <img src={resolveCoverUrl(b.cover_front_url)} alt={b.title} className="h-full w-full object-cover" />
                            : <div className="flex h-full w-full items-center justify-center bg-secondary text-primary"><BookOpen className="h-6 w-6" /></div>}
                        </div>
                        <div className="mt-1 line-clamp-2 text-[10.5px] font-semibold leading-tight">{b.title}</div>
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-success">
                          <CheckCircle2 className="h-3 w-3" /> Claim
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-start gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  <Clock className="h-3 w-3" /> Not subscribed
                </div>
                <div className="mt-1 text-[15px] font-semibold tracking-tight">Become a Weekly Maven</div>
                <p className="mt-1 text-[12px] text-muted-foreground">Pick any unread book from your favourite genre, every week.</p>
              </div>
              <button onClick={startMaven} data-testid="bs-account-maven-subscribe"
                      className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[12.5px] font-semibold text-primary-foreground transition hover:bg-primary/90">
                <Sparkles className="h-4 w-4" /> Subscribe
              </button>
            </div>
          )}
        </div>

        {/* Bookshelf */}
        <div className="mt-8">
          <div className="mb-3 flex items-end justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">My Bookshelf</div>
              <h2 className="text-2xl font-semibold tracking-tight">{shelf?.length || 0} books</h2>
            </div>
            <Link to="/bookstore" data-testid="bs-account-browse"
                  className="inline-flex items-center gap-1 rounded-sm px-2.5 py-1 text-[12px] text-primary hover:underline">
              <ShoppingBag className="h-3 w-3" /> Browse the store
            </Link>
          </div>
          {!shelf ? (
            <div className="flex h-32 items-center text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading shelf…</div>
          ) : shelf.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-8 text-center text-[12.5px] text-muted-foreground">
              Your shelf is empty. <Link to="/bookstore" className="font-semibold text-primary hover:underline">Find a book to start with.</Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {shelf.map((e) => <ShelfCard key={e.id} entry={e} onOpen={setOpen} />)}
            </div>
          )}
        </div>
      </section>

      {/* iter-p3-l — Wishlist panel. Users can manage saved titles
       *  and see which language they're waiting on. */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 pb-8">
        <WishlistPanel onOpen={setOpen} />
      </section>

      {/* iter-romance28 — Reader Report Card. */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 pb-8">
        <ReaderReportCard />
      </section>

      {/* iter-p3-g — Reading language preference. Powers localized
       *  purchase / bundle / drip emails (backend
       *  `_resolve_buyer_locale`). */}
      {/* iter-p3-g — Reading language picker. */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 pb-8">
        <ReadingLanguagePicker />
      </section>

      {/* Feb-2026 (indie-conv batch2 pt3 fu) — Heat preference panel. */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 pb-8">
        <HeatPreferencePanel />
      </section>



      {/* iter-romance25 — Reader warning manager. Lists every content
       *  tag the reader has excluded on any shelf so they can curate
       *  the set without revisiting each genre page individually. */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 pb-12">
        <ReaderWarningManager />
      </section>

      {open && <BookDetailModal book={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/* iter-p3-l — Reader wishlist panel.  Displays saved titles + the
 * language each entry is waiting for; readers can jump into the
 * book detail modal or remove a row without leaving the account
 * page. */
function WishlistPanel({ onOpen }) {
  const [rows, setRows] = React.useState(null);
  const [busy, setBusy] = React.useState(null); // book_id currently being removed
  const reload = React.useCallback(() => {
    bookstoreApi.wishlist()
      .then((d) => setRows(d?.wishlist || []))
      .catch(() => setRows([]));
  }, []);
  React.useEffect(() => { reload(); }, [reload]);
  const remove = async (row) => {
    setBusy(row.id);
    try {
      await bookstoreApi.wishlistRemove(row.book_id, row.desired_language || null);
      toast.success('Removed from your wishlist.');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove.');
    } finally { setBusy(null); }
  };
  if (rows === null) {
    return (
      <div className="rounded-lg border border-border bg-card p-5" data-testid="bs-wishlist-loading">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-border bg-card p-4 sm:p-5" data-testid="bs-wishlist-panel">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Wishlist</div>
          <h3 className="mt-1 text-[15px] font-semibold tracking-tight">
            {rows.length === 0
              ? 'No saved books yet — heart any title to save it here.'
              : `${rows.length} book${rows.length === 1 ? '' : 's'} you're waiting on`}
          </h3>
        </div>
      </div>
      {rows.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="bs-wishlist-list">
          {rows.map((row) => (
            <li key={row.id} className="flex items-center gap-2 rounded-sm border border-border bg-panel-header/20 px-2 py-1.5 text-[12.5px]" data-testid={`bs-wishlist-row-${row.id}`}>
              <button
                type="button"
                onClick={() => row.book && onOpen(row.book)}
                className="flex min-w-0 flex-1 items-center gap-2 text-left hover:opacity-80"
                data-testid={`bs-wishlist-open-${row.id}`}
              >
                {row.book?.cover_front_url && (
                  <img
                    src={resolveCoverUrl(row.book.cover_front_url)}
                    alt=""
                    className="h-10 w-7 flex-shrink-0 rounded-sm object-cover"
                  />
                )}
                <span className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{row.book?.title || row.book_id}</div>
                  <div className="truncate text-[10.5px] text-muted-foreground">
                    {row.book?.author}
                    {row.desired_language && (
                      <span className="ml-2 rounded bg-primary/10 px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-wider text-primary">
                        Waiting for {row.desired_language.toUpperCase()}
                      </span>
                    )}
                    {row.notified_at && (
                      <span className="ml-2 text-emerald-600">✓ Notified</span>
                    )}
                  </div>
                </span>
              </button>
              <button
                type="button"
                onClick={() => remove(row)}
                disabled={busy === row.id}
                data-testid={`bs-wishlist-remove-${row.id}`}
                className="rounded-sm border border-border p-1 text-muted-foreground hover:border-oxblood/40 hover:text-oxblood disabled:opacity-40"
                aria-label="Remove from wishlist"
                title="Remove from wishlist"
              >
                {busy === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* iter-p3-g — Reading language picker.  Writes to
 * `bookstore_users.preferred_language` via the mobile prefs
 * endpoint. Any purchase / bundle / drip email that follows this
 * update is delivered in the chosen language.
 */
function ReadingLanguagePicker() {
  const { user, refresh } = useBookstoreAuth();
  const [value, setValue] = React.useState((user && user.preferred_language) || '');
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { setValue((user && user.preferred_language) || ''); }, [user]);
  if (!user) return null;
  const save = async (code) => {
    setSaving(true);
    try {
      await bookstoreApi.updatePreferences({ preferred_language: code || '' });
      setValue(code);
      toast.success(code
        ? `Reading language set to ${BOOKSTORE_READING_LANGUAGES.find((l) => l.code === code)?.label || code}. Purchase emails will follow this language.`
        : 'Reading language cleared. Emails default to the book\'s own language.');
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update reading language.');
    } finally { setSaving(false); }
  };
  const isRTL = (value === 'ar' || value === 'he');
  return (
    <div className="rounded-lg border border-border bg-card p-4 sm:p-5" data-testid="bs-reading-language-panel">
      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Reading language</div>
      <h3 className="mt-1 text-[15px] font-semibold tracking-tight">
        Emails and store copy will follow this language
      </h3>
      <p className="mt-1 text-[12px] text-muted-foreground">
        Purchase, bundle, and drip emails go out in your chosen language.
        The book itself is delivered in whatever language its author published.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="bs-reading-language-chips">
        {BOOKSTORE_READING_LANGUAGES.map((l) => {
          const active = (value || '') === l.code;
          return (
            <button
              key={l.code || 'any'}
              type="button"
              onClick={() => save(l.code)}
              disabled={saving}
              data-testid={`bs-reading-language-${l.code || 'any'}`}
              dir={l.rtl ? 'rtl' : undefined}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-[3px] text-[11px] uppercase tracking-wider transition disabled:opacity-60 ${
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-secondary/50 text-muted-foreground hover:border-primary/60 hover:text-foreground'
              }`}
            >
              {l.label}
            </button>
          );
        })}
      </div>
      {isRTL && (
        <div className="mt-3 rounded-md border border-amber-400/40 bg-amber-50/60 p-3 text-[11.5px] text-amber-900" data-testid="bs-reading-language-rtl-note">
          Arabic is a right-to-left script — email templates will render right-aligned.
        </div>
      )}
    </div>
  );
}

/* iter-romance28 — Reader Report Card. Aggregates the reader's
   bookshelf via `/api/bookstore/report-card` into a friendly "N books,
   mostly X shelf, mostly Y archetype" widget. Renders top 3 shelves
   as a mini bar chart + top 5 tags as chips. */
function ReaderReportCard() {
  const [card, setCard] = React.useState(null);
  React.useEffect(() => {
    bookstoreApi.reportCard()
      .then((r) => setCard(r))
      .catch(() => setCard({ book_count: 0, shelves: [], tags: [] }));
  }, []);
  if (!card) {
    return (
      <div className="rounded-lg border border-border bg-card p-5" data-testid="bs-report-card-loading">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!card.book_count) {
    return (
      <div className="rounded-lg border border-border bg-card p-5 text-[12px] text-muted-foreground" data-testid="bs-report-card-empty">
        <BarChart3 className="mb-1 h-4 w-4 text-oxblood" />
        Your Reader Report Card will unlock once you own your first book. Head to <Link to="/bookstore" className="underline hover:text-foreground">the shelves</Link>.
      </div>
    );
  }
  const topShelf = card.top_shelf || {};
  const topTag   = card.top_tag   || {};
  const shelfMax = Math.max(...(card.shelves || []).map((s) => s.count), 1);
  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid="bs-report-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-oxblood">
            <BarChart3 className="h-3 w-3" /> Reader Report Card
          </div>
          <div className="mt-1 text-[15px] font-semibold tracking-tight" data-testid="bs-report-card-headline">
            You&rsquo;ve read <span className="text-primary">{card.book_count}</span> book{card.book_count === 1 ? '' : 's'}
            {topShelf.name && <> — mostly on <span className="text-primary">{topShelf.name}</span></>}
            {topTag.tag && <> — mostly <em className="text-primary">{topTag.tag}</em></>}.
          </div>
        </div>
        <Heart className="h-4 w-4 text-oxblood/70" />
      </div>
      {(card.shelves || []).length > 0 && (
        <div className="mt-4">
          <div className="mb-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Top shelves</div>
          <div className="space-y-1" data-testid="bs-report-card-shelves">
            {card.shelves.slice(0, 5).map((s) => (
              <div key={s.slug} className="flex items-center gap-2">
                <Link to={`/bookstore/g/${s.slug}`} className="w-32 truncate text-[11.5px] font-medium hover:underline">{s.name}</Link>
                <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                  <div className="absolute inset-y-0 left-0 bg-oxblood"
                       style={{ width: `${(s.count / shelfMax) * 100}%` }} />
                </div>
                <div className="w-8 text-right text-[10.5px] font-mono-stat text-muted-foreground">{s.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {(card.tags || []).length > 0 && (
        <div className="mt-4">
          <div className="mb-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Your archetypes</div>
          <div className="flex flex-wrap gap-1" data-testid="bs-report-card-tags">
            {card.tags.slice(0, 8).map((t) => (
              <span key={t.tag}
                    className="inline-flex items-center gap-1 rounded-full border border-oxblood/40 bg-oxblood/[0.08] px-2 py-0.5 text-[10.5px] uppercase tracking-wider text-oxblood">
                {t.tag} <span className="opacity-70">·{t.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* iter-romance25 — Reader warning manager. */
function MatureAudiencesShelfToggle() {
  const { hidden, setHidden } = useHideMatureShelves();

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4" data-testid="bs-mature-shelf-preference">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Mature audiences shelf</div>
        <div className="mt-1 text-[13px] text-foreground">Hide the 18+ shelf across the Bookstore</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={hidden}
        onClick={() => setHidden(!hidden)}
        data-testid="bs-mature-shelf-toggle"
        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${hidden ? 'bg-oxblood' : 'bg-muted'}`}
      >
        <span
          className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${hidden ? 'translate-x-6' : 'translate-x-1'}`}
        />
        <span className="sr-only">{hidden ? 'Show' : 'Hide'} mature audiences shelf</span>
      </button>
    </div>
  );
}

function ReaderWarningManager() {
  const { excluded, hasPreferences, clearAll } = useReaderTagPreferences();

  // Detail rows: which shelf added the tag + when. Read from
  // localStorage directly (no API round-trip — preferences are
  // deliberately on-device).
  const rows = React.useMemo(() => {
    if (excluded.size === 0) return [];
    const out = [];
    try {
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith('bs-shelf-excluded-tags:')) continue;
        const slug = key.slice('bs-shelf-excluded-tags:'.length);
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        try {
          JSON.parse(raw).forEach((tag) => out.push({ tag, slug }));
        } catch { /* skip */ }
      }
    } catch { /* SSR */ }
    return out.sort((a, b) => a.tag.localeCompare(b.tag));
  }, [excluded]);

  // Fire the contextual tour on first mount so returning readers see
  // a one-time explainer for the new manager.
  React.useEffect(() => { runTour('bookstore-account-warning-manager-open'); }, []);

  const removeTagFromShelf = (tag, slug) => {
    const key = `bs-shelf-excluded-tags:${slug}`;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const next = JSON.parse(raw).filter((t) => t !== tag);
      if (next.length) localStorage.setItem(key, JSON.stringify(next));
      else             localStorage.removeItem(key);
      // Trigger `storage` listeners in this tab too.
      window.dispatchEvent(new StorageEvent('storage', { key }));
    } catch { /* noop */ }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4 sm:p-5" data-testid="bs-account-warning-manager">
      {/* Feb-2026 · Mature-Audiences super-tile toggle. Sits above the
       *  per-tag "Reader warnings" list because it hides an entire
       *  shelf, not individual tags. */}
      <MatureAudiencesShelfToggle />

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-oxblood" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Reader warnings</div>
            <div className="text-[15px] font-semibold tracking-tight">Manage what we hide</div>
          </div>
        </div>
        {hasPreferences && (
          <CoachHint
            label="What next?"
            body="Preferences apply across every shelf, not just the one you clicked from. Come back here anytime to see the full list — or hit 'Show everything' to start fresh."
            testId="bs-warning-clear-hint"
          >
            <button
              onClick={clearAll}
              data-testid="bs-account-warning-clear-all"
              className="rounded-sm border border-oxblood px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-oxblood hover:bg-oxblood/10"
            >
              Show everything
            </button>
          </CoachHint>
        )}
      </div>

      {!hasPreferences ? (
        <p className="mt-3 text-[12px] text-muted-foreground" data-testid="bs-account-warning-empty">
          You haven&apos;t hidden anything yet. Visit a shelf and click a tag chip (e.g. <em>dubcon</em>, <em>violence</em>) to filter titles carrying that tag site-wide. Your preferences save automatically.
        </p>
      ) : (
        <div className="mt-3 space-y-1.5" data-testid="bs-account-warning-list">
          {rows.map(({ tag, slug }) => (
            <div
              key={`${slug}:${tag}`}
              className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-1.5"
              data-testid={`bs-warning-row-${slug}-${tag.replace(/\s+/g, '-')}`}
            >
              <div className="min-w-0 flex-1">
                <span className="inline-flex items-center rounded-full border border-oxblood/40 bg-oxblood/10 px-2 py-0.5 text-[10.5px] font-medium uppercase tracking-wider text-oxblood">
                  {tag}
                </span>
                <span className="ml-2 text-[10.5px] text-muted-foreground">
                  hidden on <Link to={`/bookstore/g/${slug}`} className="underline hover:text-foreground">{slug.replace(/-/g, ' ')}</Link>
                </span>
              </div>
              <button
                onClick={() => removeTagFromShelf(tag, slug)}
                data-testid={`bs-warning-remove-${slug}-${tag.replace(/\s+/g, '-')}`}
                className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-primary"
              >
                Show
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
